import { useState, useContext } from 'react';
import { ethers } from 'ethers';
import { AuthContext } from '../../AuthContext';

// Token Configuration
const SUPPORTED_TOKENS = {
  USD1: 'USD1 (Gold/Reserve Backed)',
  USDT: 'USDT (Reserves Backed)',
  USDC: 'USDC (Government-Reg Backed)',
  DAI: 'DAI (Collateral Backed)',
  FDUSD: 'FDUSD (Reserves Backed)',
  EURT: 'EURT (Euro Reserves Backed)',
  WETH: 'WETH (Wrapped Ethereum)'
};

// ERC20 ABI for token operations
const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
  "function balanceOf(address account) external view returns (uint256)"
];

export default function BankPay({
  walletAddress,
  web3payContract,
  contractData,
  balances,
  refreshBalances
}) {
  const { isAuthenticated, ensName, address } = useContext(AuthContext);

  // Form state
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [token, setToken] = useState('USD1');
  const [isInStore, setIsInStore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generated QR state
  const [qrCode, setQrCode] = useState('');
  const [qrId, setQrId] = useState('');
  const [paymentData, setPaymentData] = useState(null);

  // Input validation
  const validateInputs = () => {
    if (!amount || !recipient || !token) {
      return 'Please enter amount, recipient, and select token';
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return 'Amount must be a number greater than 0';
    }

    if (!ethers.isAddress(recipient) && !recipient.includes('.')) {
      return 'Please enter a valid Ethereum address or ENS name';
    }

    return null; // No errors
  };

  // Contract-based QR generation
  const handleContractQRGeneration = async () => {
    if (!web3payContract || !contractData?.tokenAddresses) {
      setError('Web3Pay contract not available. Please check your connection.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tokenAddress = contractData.tokenAddresses[token];
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error(`Token ${token} is not configured in the contract`);
      }

      // Validate recipient address
      if (!ethers.isAddress(recipient) && !recipient.endsWith('.eth')) {
        throw new Error('Please enter a valid Ethereum address or ENS name');
      }

      // Get token decimals
      const provider = new ethers.BrowserProvider(window.ethereum);
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);

      let decimals = 18; // Default fallback
      try {
        decimals = await tokenContract.decimals();
      } catch (decimalsError) {
        console.warn('Could not fetch decimals, using default 18:', decimalsError);
      }

      const amountInWei = ethers.parseUnits(amount, decimals);

      // Generate unique QR reference
      const qrReference = `qr_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      console.log('Creating QR payment with:', {
        recipient,
        token: tokenAddress,
        amount: amountInWei.toString(),
        tokenSymbol: token,
        qrReference,
        isInStore
      });

      // Create QR payment
      const tx = await web3payContract.createQRPayment(
        recipient,
        tokenAddress,
        amountInWei,
        qrReference,
        isInStore
      );

      console.log('Transaction sent:', tx.hash);

      const receipt = await tx.wait();
      console.log('Transaction confirmed:', receipt);

      // Try to extract QR ID from events
      let qrId = null;
      try {
        // Method 1: Try to parse events using contract interface
        if (web3payContract.interface) {
          for (const log of receipt.logs) {
            try {
              const parsedLog = web3payContract.interface.parseLog(log);
              if (parsedLog && parsedLog.name === 'QRPaymentCreated') {
                qrId = parsedLog.args.qrId.toString();
                break;
              }
            } catch (parseError) {
              // Continue to next log
              continue;
            }
          }
        }

        // Method 2: If no event found, use transaction hash as reference
        if (!qrId) {
          qrId = `tx_${receipt.hash.substring(0, 10)}`;
          console.warn('QRPaymentCreated event not found, using transaction hash as ID');
        }
      } catch (eventError) {
        console.error('Error parsing events:', eventError);
        qrId = `tx_${receipt.hash.substring(0, 10)}`;
      }

      // Generate simple QR code data
      const qrData = {
        type: 'web3pay',
        contract: web3payContract.target,
        qrId,
        amount,
        token,
        recipient,
        reference: qrReference,
        timestamp: Date.now(),
        network: 'Sepolia'
      };

      // Create simple QR code (text representation)
      const qrCodeSvg = generateSimpleQRCode(qrData);

      setQrId(qrId);
      setQrCode(qrCodeSvg);
      setPaymentData(qrData);

      console.log(`QR payment created: #${qrId} for ${amount} ${token}`);

      // Refresh balances if callback provided
      if (refreshBalances && typeof refreshBalances === 'function') {
        await refreshBalances();
      }

    } catch (error) {
      console.error('Contract QR generation failed:', error);
      setError(`QR creation failed: ${error.message || error.reason || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Simple QR code generator
  const generateSimpleQRCode = (qrData) => {
    const qrText = JSON.stringify(qrData);
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#ffffff"/>
        <text x="100" y="80" text-anchor="middle" fill="#000000" font-family="Arial" font-size="12" font-weight="bold">
          Web3Pay QR
        </text>
        <text x="100" y="100" text-anchor="middle" fill="#000000" font-family="Arial" font-size="10">
          ${qrData.amount} ${qrData.token}
        </text>
        <text x="100" y="115" text-anchor="middle" fill="#000000" font-family="Arial" font-size="8">
          ID: ${qrData.qrId}
        </text>
        <text x="100" y="130" text-anchor="middle" fill="#000000" font-family="Arial" font-size="8">
          Sepolia Testnet
        </text>
        <rect x="10" y="10" width="180" height="180" fill="none" stroke="#000000" stroke-width="2"/>
      </svg>
    `)}`;
  };

  // Handle QR generation
  const handleGenerateQR = async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    await handleContractQRGeneration();
  };

  // Calculate fee and net amount (using contract fee)
  const feePercent = contractData?.feePercent || 0.5;
  const feeAmount = amount ? (parseFloat(amount) * feePercent) / 100 : 0;
  const netAmount = amount ? parseFloat(amount) - feeAmount : 0;

  if (!isAuthenticated) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        background: '#111',
        border: '1px solid #ffd700',
        borderRadius: '8px',
        padding: '30px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ffd700', marginBottom: '20px' }}>
          Web3Pay - Authentication Required
        </h2>
        <p style={{ color: '#ccc', marginBottom: '30px' }}>
          Please connect your wallet to access the payment system.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#FFD700', marginBottom: '5px' }}>
            Web3Pay Smart Contract
          </h2>
          <p style={{ color: '#ccc' }}>
            Generate QR codes for on-chain payments with smart contract integration on Sepolia Testnet.
          </p>
        </div>
        <div style={{
          background: 'rgba(0, 255, 0, 0.1)',
          border: '1px solid #00ff00',
          borderRadius: '6px',
          padding: '5px 10px',
          fontSize: '12px',
          color: '#00ff00'
        }}>
          Contract Mode
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid #ff4444',
          color: '#ff6666',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '30px',
        marginBottom: '30px'
      }}>
        {/* Payment Form */}
        <div style={{
          background: '#111',
          border: '1px solid #FFD700',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '15px' }}>Create Payment QR</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
              Amount
            </label>
            <input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.001"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ffd700',
                borderRadius: '8px',
                background: loading ? '#222' : '#1a1a2e',
                color: loading ? '#666' : '#ffd700',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
              Recipient Address/ENS
            </label>
            <input
              placeholder="0x... or name.eth"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ffd700',
                borderRadius: '8px',
                background: loading ? '#222' : '#1a1a2e',
                color: loading ? '#666' : '#ffd700',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
              Token
            </label>
            <select
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ffd700',
                borderRadius: '8px',
                background: loading ? '#222' : '#1a1a2e',
                color: loading ? '#666' : '#ffd700',
                fontSize: '14px'
              }}
            >
              {Object.entries(SUPPORTED_TOKENS).map(([key, label]) => (
                <option key={key} value={key} style={{ background: '#1a1a2e', color: '#ffd700' }}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', color: '#FFD700', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isInStore}
                onChange={(e) => setIsInStore(e.target.checked)}
                disabled={loading}
                style={{ marginRight: '8px' }}
              />
              In-Store Payment Mode
            </label>
          </div>

          {/* Fee Preview */}
          {amount && (
            <div style={{
              background: '#222',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px',
              border: '1px solid #444'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ color: '#ccc' }}>Amount:</span>
                <span style={{ color: '#FFD700' }}>{amount} {token}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ color: '#ccc' }}>Fee ({feePercent}%):</span>
                <span style={{ color: '#FF6B6B' }}>{feeAmount.toFixed(6)} {token}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #444', paddingTop: '5px' }}>
                <span style={{ color: '#ccc', fontWeight: 'bold' }}>Net Amount:</span>
                <span style={{ color: '#00FF00', fontWeight: 'bold' }}>{netAmount.toFixed(6)} {token}</span>
              </div>
            </div>
          )}

          <button
            onClick={handleGenerateQR}
            disabled={loading || !amount || !recipient || !token}
            style={{
              width: '100%',
              background: loading || !amount || !recipient || !token
                ? '#444'
                : 'linear-gradient(135deg, #ffd700, #ffed4e)',
              color: loading || !amount || !recipient || !token ? '#666' : 'black',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: loading || !amount || !recipient || !token ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {loading ? 'Creating QR on Blockchain...' : 'Generate QR Code'}
          </button>

          {/* Contract Info */}
          <div style={{
            marginTop: '15px',
            padding: '10px',
            background: 'rgba(255, 215, 0, 0.05)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 215, 0, 0.3)'
          }}>
            <div style={{ fontSize: '11px', color: '#ffd700', textAlign: 'center' }}>
              Powered by Web3Pay Smart Contract on Sepolia
            </div>
          </div>
        </div>

        {/* Generated QR Display */}
        <div style={{
          background: '#111',
          border: '1px solid #FFD700',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '15px' }}>Generated QR Code</h3>

          {qrCode ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '15px',
                display: 'inline-block'
              }}>
                <img
                  src={qrCode}
                  alt="Payment QR Code"
                  style={{ width: '200px', height: '200px' }}
                />
              </div>

              {paymentData && (
                <div style={{
                  background: '#222',
                  padding: '15px',
                  borderRadius: '4px',
                  textAlign: 'left'
                }}>
                  <h4 style={{ color: '#FFD700', marginBottom: '10px' }}>Payment Details</h4>
                  <div style={{ marginBottom: '5px', color: '#ccc' }}>
                    QR ID: <span style={{ color: '#FFD700' }}>{paymentData.qrId}</span>
                  </div>
                  <div style={{ marginBottom: '5px', color: '#ccc' }}>
                    Amount: <span style={{ color: '#FFD700' }}>{paymentData.amount} {paymentData.token}</span>
                  </div>
                  <div style={{ marginBottom: '5px', color: '#ccc' }}>
                    To: <span style={{ color: '#FFD700' }}>{paymentData.recipient}</span>
                  </div>
                  <div style={{ marginBottom: '5px', color: '#ccc' }}>
                    Network: <span style={{ color: '#00FF00' }}>{paymentData.network}</span>
                  </div>
                  <div style={{ color: '#ccc', fontSize: '12px', marginTop: '10px' }}>
                    Contract: {paymentData.contract.substring(0, 10)}...
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              color: '#666',
              padding: '50px 20px'
            }}>
              <p>Generate a payment to see QR code here</p>
              <div style={{ fontSize: '12px', marginTop: '10px', color: '#ffd700' }}>
                QR codes are stored on the blockchain
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
